import React from 'react';
import {
    Box,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';
import { ReactComponent as VirtualizedTableCheckboxBlankIcon } from '../../static/images/svg/material-symbols_check-box-outline-blank-1.svg';
import { ReactComponent as VirtualizedTableCheckboxCheckedIcon } from '../../static/images/svg/material-symbols_check-box-rounded.svg';
import { ReactComponent as VirtualizedTableCheckboxIndeterminateIcon } from '../../static/images/svg/material-symbols_indeterminate-check-box-rounded.svg';

/** 自定义勾选图标尺寸（与 SVG viewBox 一致） */
const VIRTUALIZED_TABLE_CHECKBOX_ICON_PX = 28;

/** HTML5 行拖拽 dataTransfer 类型（避免与文本拖放冲突） */
const VIRTUALIZED_TABLE_ROW_DND_MIME = 'application/x-virtualized-mui-table-row-index';

/** 行拖拽把手列默认宽度（px） */
const VIRTUALIZED_TABLE_ROW_DRAG_COL_WIDTH = 44;

/** 拖拽会话期间挂到 `document.documentElement`，配合注入样式统一为 `grabbing`，避免 `not-allowed` 等手势 */
const VMT_ROW_DND_HTML_ATTR = 'data-vmt-row-dnd';
const VMT_ROW_DND_STYLE_ID = 'vmt-row-dnd-cursor-style';

function ensureRowDndGlobalCursorStyle() {
    if (typeof document === 'undefined') {
        return;
    }
    if (document.getElementById(VMT_ROW_DND_STYLE_ID)) {
        return;
    }
    const el = document.createElement('style');
    el.id = VMT_ROW_DND_STYLE_ID;
    /** 根 + 表内常见可禁用控件：不用 `*`，减轻与原生拖拽抢光标；仍覆盖行内 Checkbox 的 `not-allowed` */
    el.textContent = [
        `html[${VMT_ROW_DND_HTML_ATTR}="1"]{cursor:grabbing!important;}`,
        `html[${VMT_ROW_DND_HTML_ATTR}="1"] .MuiCheckbox-root,`,
        `html[${VMT_ROW_DND_HTML_ATTR}="1"] .MuiButtonBase-root.Mui-disabled`,
        `{cursor:grabbing!important;}`,
    ].join('');
    document.head.appendChild(el);
}

/** 捕获阶段统一接受 `move`，避免经过滚动区/表间隙/子元素时出现禁止手势 */
function rowDndDocumentAcceptCapture(e: DragEvent) {
    e.preventDefault();
    const dt = e.dataTransfer;
    if (dt) {
        try {
            dt.dropEffect = 'move';
        } catch {
            /* ignore */
        }
    }
}

let rowDndDocDragCaptureAttached = false;

function attachRowDndDocumentDragCapture() {
    if (typeof document === 'undefined' || rowDndDocDragCaptureAttached) {
        return;
    }
    document.addEventListener('dragover', rowDndDocumentAcceptCapture, true);
    /** `dragenter` 未处理时部分浏览器会先闪一帧禁止光标，再进入 `dragover` */
    document.addEventListener('dragenter', rowDndDocumentAcceptCapture, true);
    rowDndDocDragCaptureAttached = true;
}

function detachRowDndDocumentDragCapture() {
    if (typeof document === 'undefined' || !rowDndDocDragCaptureAttached) {
        return;
    }
    document.removeEventListener('dragover', rowDndDocumentAcceptCapture, true);
    document.removeEventListener('dragenter', rowDndDocumentAcceptCapture, true);
    rowDndDocDragCaptureAttached = false;
}

function setRowDndGlobalCursor(active: boolean) {
    if (typeof document === 'undefined') {
        return;
    }
    if (active) {
        ensureRowDndGlobalCursorStyle();
        document.documentElement.setAttribute(VMT_ROW_DND_HTML_ATTR, '1');
        attachRowDndDocumentDragCapture();
    } else {
        document.documentElement.removeAttribute(VMT_ROW_DND_HTML_ATTR);
        detachRowDndDocumentDragCapture();
    }
}

/**
 * 用整行外观作为拖拽幽灵图（`tr` 须包在 `table` 内以兼容浏览器）
 * @param e 把手上的 dragstart
 * @param sourceRow 当前数据行 `tr`
 */
function setTableRowDragImage(e: React.DragEvent, sourceRow: HTMLTableRowElement) {
    const dt = e.dataTransfer;
    if (!dt?.setDragImage) {
        return;
    }
    const rect = sourceRow.getBoundingClientRect();
    const wrap = document.createElement('div');
    wrap.style.cssText = `position:fixed;left:0;top:-10000px;z-index:2147483647;pointer-events:none;width:${rect.width}px;opacity:0.98;box-shadow:0 6px 20px rgba(0,0,0,0.12);border-radius:4px;overflow:hidden;background:var(--fill-neutral-100,#fff);`;
    const table = document.createElement('table');
    table.style.cssText = `width:${rect.width}px;border-collapse:separate;table-layout:fixed;margin:0;`;
    const tbody = document.createElement('tbody');
    const clone = sourceRow.cloneNode(true) as HTMLTableRowElement;
    clone.style.width = `${rect.width}px`;
    tbody.appendChild(clone);
    table.appendChild(tbody);
    wrap.appendChild(table);
    document.body.appendChild(wrap);
    const ox = Math.min(Math.max(0, e.clientX - rect.left), Math.max(1, rect.width) - 1);
    const oy = Math.min(Math.max(0, e.clientY - rect.top), Math.max(1, rect.height) - 1);
    try {
        dt.setDragImage(wrap, ox, oy);
    } finally {
        window.setTimeout(() => wrap.remove(), 0);
    }
}

/** `TableRow` / 拖动手柄共用的运行时上下文（含拖拽中索引，用于整行样式） */
type VirtualizedMuiTableRowReorderRuntimeContext = Pick<
    VirtualizedMuiTableRowReorder,
    'onReorder' | 'getDragHandleAriaLabel'
> & {
    /** 正在拖拽的数据行索引；与 `data-item-index` 对齐 */
    draggingRowIndex: number | null;
    setDraggingRowIndex: (index: number | null) => void;
    /** 结束拖拽：清索引 + 恢复光标（drop / dragend / 兜底） */
    endRowDragSession: () => void;
};

/**
 * 行拖拽排序：与虚拟列表兼容的 HTML5 DnD；由父组件 `enabled` 控制开关
 */
export type VirtualizedMuiTableRowReorder = {
    /** 为 true 时首列显示拖动手柄，行可拖放调整顺序 */
    enabled: boolean;
    /**
     * 落位回调：`rows` 中的数据行索引（与 `itemContent` 的 index 一致）
     * @param fromIndex 拖拽起始行索引
     * @param toIndex 释放目标行索引
     */
    onReorder: (fromIndex: number, toIndex: number) => void;
    /** 拖动手柄 `aria-label` */
    getDragHandleAriaLabel?: () => string;
};

const virtualizedTableCheckboxSx = {
    p: '6px',
    color: 'primary.main',
    '& svg': {
        width: VIRTUALIZED_TABLE_CHECKBOX_ICON_PX,
        height: VIRTUALIZED_TABLE_CHECKBOX_ICON_PX,
        display: 'block',
    },
} as const;

/**
 * 列定义：表头与每行单元格渲染
 * @template T 行数据类型
 */
export interface VirtualizedMuiTableColumn<T> {
    /** 列唯一 key，用于 React 列表 */
    id: string;
    /** 表头展示内容 */
    header: React.ReactNode;
    /** 单元格水平对齐 */
    align?: 'left' | 'right' | 'center';
    /** 列宽（配合 table-layout: fixed 更稳定） */
    width?: number | string;
    /**
     * 渲染数据单元格
     * @param row 当前行
     * @param rowIndex 行索引（仅数据行，不含表头）
     */
    renderCell: (row: T, rowIndex: number) => React.ReactNode;
}

export interface VirtualizedMuiTableProps<T> {
    /** 列配置 */
    columns: VirtualizedMuiTableColumn<T>[];
    /** 数据行（loading 时传空数组即可） */
    rows: T[];
    /** 为 true 时显示加载占位，不渲染数据行 */
    loading?: boolean;
    /** 加载中文案 */
    loadingText?: React.ReactNode;
    /** 无数据且非 loading 时的文案 */
    emptyText?: React.ReactNode;
    /** 无数据且非 loading 时的自定义内容，传入后替代默认空文案 */
    emptyContent?: React.ReactNode;
    /**
     * 行 key
     * @param row 当前行
     * @param index 索引
     */
    getRowKey: (row: T, index: number) => React.Key;
    /** MUI Table size */
    size?: 'small' | 'medium';
    /** Virtuoso 根节点高度（须为确定值或父级已限高时的 100%） */
    height?: string | number;
    /** 预估行高（px），行高不固定时可略增大以减少滚动纠偏 */
    defaultItemHeight?: number;
    /** 额外增加的可视区域外预渲染行数 */
    increaseViewportBy?: number;
    /** 根节点 className */
    className?: string;
    /** Table 的 className */
    tableClassName?: string;
    /**
     * 行多选：在首列增加 checkbox，表头支持全选当前 `rows` 中可选行
     * （与 `getRowKey` 一致作为选中标识）
     */
    checkboxSelection?: VirtualizedMuiTableCheckboxSelection<T>;
    /**
     * 行拖拽排序（HTML5 DnD，与 react-virtuoso 兼容）；`enabled` 由外部控制（如仅编辑模式开启）
     */
    rowReorder?: VirtualizedMuiTableRowReorder;
}

/**
 * 虚拟表格多选配置
 * @template T 行数据类型
 */
export interface VirtualizedMuiTableCheckboxSelection<T> {
    /** 已选行的 key 列表（与 `getRowKey` 返回值对应） */
    selectedKeys: readonly React.Key[];
    /** 选中集合变化 */
    onChange: (keys: React.Key[]) => void;
    /** 返回 false 时该行不渲染可选 checkbox，且不计入表头全选范围 */
    isRowSelectable?: (row: T, rowIndex: number) => boolean;
    /** 勾选列宽度，默认 64（含 40px 图标与内边距） */
    columnWidth?: number | string;
}

function selectionKeySet(keys: readonly React.Key[]): Set<string> {
    return new Set(keys.map((k) => String(k)));
}

const defaultTableComponents: TableComponents = {
    Scroller: React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function VirtuosoTableScroller(
        props,
        ref,
    ) {
        return <TableContainer {...props} ref={ref} sx={{ height: '100%', overflow: 'auto' }} />;
    }),
    Table: (props) => <Table {...props} size="small" sx={{ borderCollapse: 'separate', tableLayout: 'fixed', width: '100%' }} />,
    TableHead: React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(function VirtuosoTableHead(
        props,
        ref,
    ) {
        return <TableHead {...props} ref={ref} />;
    }),
    TableRow: (props: React.ComponentProps<typeof TableRow> & { item?: unknown }) => {
        const { item, ...rowProps } = props;
        void item;
        return <TableRow {...rowProps} hover />;
    },
    TableBody: React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(function VirtuosoTableBody(
        props,
        ref,
    ) {
        return <TableBody {...props} ref={ref} />;
    }),
};

/** 行排序时滚动容器（Virtuoso `Scroller`）上兜底 `dragover`，覆盖行与行之间的空白与虚拟列表内边距 */
const VirtuosoTableScrollerWithRowDnd = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    function VirtuosoTableScrollerWithRowDnd(props, ref) {
        const tcProps = props as React.ComponentProps<typeof TableContainer>;
        const { onDragOver: onDragOverProp, sx, ...rest } = tcProps;
        const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            try {
                e.dataTransfer.dropEffect = 'move';
            } catch {
                /* 部分环境下只读 */
            }
            onDragOverProp?.(e);
        };
        return (
            <TableContainer
                {...rest}
                ref={ref}
                onDragOver={onDragOver}
                sx={sx ? [{ height: '100%', overflow: 'auto' }, ...(Array.isArray(sx) ? sx : [sx])] : { height: '100%', overflow: 'auto' }}
            />
        );
    },
);

/** 供 `TableRow`、拖动手柄读取回调与拖拽态（Virtuoso 子树内） */
const VirtualizedMuiTableRowReorderContext = React.createContext<VirtualizedMuiTableRowReorderRuntimeContext | null>(
    null,
);

/** Virtuoso `TableRow` 与 rbd 不同，props 含 `data-item-index` 等；用宽松类型避免与 MUI `TableRow` 声明冲突 */
const VirtualizedMuiTableRowWithDrop = React.forwardRef<HTMLTableRowElement, Record<string, unknown>>(
    function VirtualizedMuiTableRowWithDrop(props, ref) {
        const ctx = React.useContext(VirtualizedMuiTableRowReorderContext);
        const { item, children, style, ...rest } = props;
        void item;
        const rowIndex = props['data-item-index'] as number;
        const sx = style as React.CSSProperties | undefined;
        if (!ctx) {
            return (
                <TableRow ref={ref} style={sx} {...(rest as object)} hover>
                    {children as React.ReactNode}
                </TableRow>
            );
        }
        const isSourceRow = ctx.draggingRowIndex === rowIndex;
        const isRowDndActive = ctx.draggingRowIndex !== null;
        return (
            <TableRow
                ref={ref}
                style={sx}
                {...(rest as object)}
                hover
                sx={
                    isRowDndActive
                        ? {
                            cursor: 'grabbing',
                            ...(isSourceRow
                                ? {
                                    opacity: 0.42,
                                    bgcolor: 'action.hover',
                                    '& > .MuiTableCell-root': {
                                        bgcolor: 'transparent',
                                    },
                                }
                                : {}),
                        }
                        : undefined
                }
                onDragOver={(e) => {
                    e.preventDefault();
                    try {
                        e.dataTransfer.dropEffect = 'move';
                    } catch {
                        /* 部分环境下只读 */
                    }
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    const from = Number(e.dataTransfer.getData(VIRTUALIZED_TABLE_ROW_DND_MIME));
                    if (!Number.isFinite(from) || from === rowIndex) {
                        ctx.endRowDragSession();
                        return;
                    }
                    /** 成功落位勿在此处 `endRowDragSession`：`dragend` 前会有一帧无会话态，浏览器易闪「禁止」手势 */
                    ctx.onReorder(from, rowIndex);
                }}
            >
                {children as React.ReactNode}
            </TableRow>
        );
    },
);

/**
 * 基于 react-virtuoso 的 MUI Table 虚拟列表，适合大数据量；表头固定（sticky 由 TableContainer 滚动实现）
 * @template T 行数据类型
 */
export function VirtualizedMuiTable<T>(props: VirtualizedMuiTableProps<T>) {
    const {
        columns,
        rows,
        loading = false,
        loadingText,
        emptyText,
        emptyContent,
        getRowKey,
        size = 'small',
        height = '100%',
        defaultItemHeight = 52,
        increaseViewportBy = 120,
        className,
        tableClassName,
        checkboxSelection,
        rowReorder,
    } = props;

    const showCheckbox = Boolean(checkboxSelection);
    /** 行拖拽列：开启且非 loading 时展示（无数据时仍保留表头占位） */
    const showRowDragCol = Boolean(rowReorder?.enabled && !loading);
    const colCount = columns.length + (showCheckbox ? 1 : 0) + (showRowDragCol ? 1 : 0);
    const checkboxWidth = checkboxSelection?.columnWidth ?? 40;

    /** 当前数据中可选行的 key（用于表头全选 / 半选态） */
    const selectableKeys = React.useMemo(() => {
        if (!checkboxSelection) {
            return [] as React.Key[];
        }
        const { isRowSelectable } = checkboxSelection;
        const list: React.Key[] = [];
        rows.forEach((row, rowIndex) => {
            if (isRowSelectable && !isRowSelectable(row, rowIndex)) {
                return;
            }
            list.push(getRowKey(row, rowIndex));
        });
        return list;
    }, [checkboxSelection, rows, getRowKey]);

    const selectedSet = React.useMemo(
        () => selectionKeySet(checkboxSelection?.selectedKeys ?? []),
        [checkboxSelection?.selectedKeys],
    );

    const headerCheckboxChecked =
        selectableKeys.length > 0 && selectableKeys.every((k) => selectedSet.has(String(k)));
    const headerCheckboxIndeterminate =
        selectableKeys.some((k) => selectedSet.has(String(k))) && !headerCheckboxChecked;

    const handleSelectAllClick = React.useCallback(() => {
        if (!checkboxSelection || loading || selectableKeys.length === 0) {
            return;
        }
        const selectableStr = new Set(selectableKeys.map((k) => String(k)));
        const prev = checkboxSelection.selectedKeys as React.Key[];
        if (headerCheckboxChecked) {
            checkboxSelection.onChange(prev.filter((k) => !selectableStr.has(String(k))));
        } else {
            const preserved = prev.filter((k) => !selectableStr.has(String(k)));
            checkboxSelection.onChange([...preserved, ...selectableKeys]);
        }
    }, [checkboxSelection, headerCheckboxChecked, loading, selectableKeys]);

    const fixedHeaderContent = React.useCallback(() => {
        const headSx = {
            backgroundColor: 'var(--fill-neutral-100)',
            fontWeight: 600,
            color: 'var(--fill-primary-300)',
            borderBottom: '1px solid var(--text-dark)',
        } as const;
        return (
            <TableRow>
                {showRowDragCol ? (
                    <TableCell
                        key="__virtualized_row_drag__"
                        padding="none"
                        variant="head"
                        width={VIRTUALIZED_TABLE_ROW_DRAG_COL_WIDTH}
                        style={{ height: 80 }}
                        sx={{
                            ...headSx,
                            width: VIRTUALIZED_TABLE_ROW_DRAG_COL_WIDTH,
                            minWidth: VIRTUALIZED_TABLE_ROW_DRAG_COL_WIDTH,
                            maxWidth: VIRTUALIZED_TABLE_ROW_DRAG_COL_WIDTH,
                            verticalAlign: 'middle',
                            textAlign: 'center',
                        }}
                    />
                ) : null}
                {showCheckbox ? (
                    <TableCell
                        key="__virtualized_checkbox__"
                        padding="none"
                        variant="head"
                        width={checkboxWidth}
                        style={{ height: 80 }}
                        sx={{
                            ...headSx,
                            width: checkboxWidth,
                            minWidth: checkboxWidth,
                            maxWidth: checkboxWidth,
                            verticalAlign: 'middle',
                            textAlign: 'center',
                        }}
                    >
                        <Checkbox
                            size="small"
                            color="primary"
                            icon={<VirtualizedTableCheckboxBlankIcon />}
                            checkedIcon={<VirtualizedTableCheckboxCheckedIcon />}
                            indeterminateIcon={<VirtualizedTableCheckboxIndeterminateIcon />}
                            indeterminate={headerCheckboxIndeterminate}
                            checked={headerCheckboxChecked}
                            disabled={loading || selectableKeys.length === 0}
                            onChange={handleSelectAllClick}
                            sx={virtualizedTableCheckboxSx}
                            inputProps={{
                                'aria-label': 'select all rows',
                            }}
                        />
                    </TableCell>
                ) : null}
                {columns.map((col) => (
                    <TableCell
                        key={col.id}
                        variant="head"
                        align={col.align ?? 'center'}
                        width={col.width}
                        style={{ height: 80 }}
                        sx={headSx}
                    >
                        {col.header}
                    </TableCell>
                ))}
            </TableRow>
        );
    }, [
        checkboxWidth,
        columns,
        handleSelectAllClick,
        headerCheckboxChecked,
        headerCheckboxIndeterminate,
        loading,
        selectableKeys.length,
        showCheckbox,
        showRowDragCol,
    ]);

    const components = React.useMemo(
        () => ({
            ...defaultTableComponents,
            ...(showRowDragCol
                ? { Scroller: VirtuosoTableScrollerWithRowDnd as (typeof defaultTableComponents)['Scroller'] }
                : {}),
            TableRow: (showRowDragCol && rowReorder
                ? VirtualizedMuiTableRowWithDrop
                : defaultTableComponents.TableRow) as TableComponents<T>['TableRow'],
            Table: (p: React.ComponentProps<typeof Table>) => {
                const { sx: passthroughSx, className: passthroughClassName, onDragOver: onDragOverProp, ...tableRest } = p;
                const onTableDragOver =
                    showRowDragCol &&
                    ((e: React.DragEvent<HTMLTableElement>) => {
                        e.preventDefault();
                        try {
                            e.dataTransfer.dropEffect = 'move';
                        } catch {
                            /* 部分环境下只读 */
                        }
                        onDragOverProp?.(e);
                    });
                return (
                    <Table
                        {...tableRest}
                        {...(onTableDragOver ? { onDragOver: onTableDragOver } : onDragOverProp ? { onDragOver: onDragOverProp } : {})}
                        size={size}
                        className={[tableClassName, passthroughClassName].filter(Boolean).join(' ') || undefined}
                        sx={[
                            { borderCollapse: 'separate', tableLayout: 'fixed', width: '100%' },
                            ...(Array.isArray(passthroughSx) ? passthroughSx : passthroughSx ? [passthroughSx] : []),
                        ]}
                    />
                );
            },
            EmptyPlaceholder: () => (
                <TableRow>
                    <TableCell colSpan={colCount} sx={{ border: 'none', textAlign: 'center' }}>
                        {loading ? (
                            <Box sx={{ py: 2, px: 1 }}>
                                <Typography variant="body2" component="div">
                                    {loadingText}
                                </Typography>
                            </Box>
                        ) : emptyContent ? (
                            emptyContent
                        ) : (
                            <Box sx={{ py: 2, px: 1 }}>
                                <Typography variant="body2" component="div">
                                    {emptyText}
                                </Typography>
                            </Box>
                        )}
                    </TableCell>
                </TableRow>
            ),
        }),
        [colCount, emptyContent, emptyText, loading, loadingText, rowReorder, showRowDragCol, size, tableClassName],
    );

    const data = loading || rows.length === 0 ? [] : rows;

    const [draggingRowIndex, setDraggingRowIndex] = React.useState<number | null>(null);

    const endRowDragSession = React.useCallback(() => {
        setDraggingRowIndex(null);
        setRowDndGlobalCursor(false);
    }, []);

    React.useEffect(() => {
        if (draggingRowIndex === null) {
            return;
        }
        window.addEventListener('dragend', endRowDragSession);
        return () => window.removeEventListener('dragend', endRowDragSession);
    }, [draggingRowIndex, endRowDragSession]);

    React.useEffect(
        () => () => {
            setRowDndGlobalCursor(false);
        },
        [],
    );

    const rowReorderContextValue: VirtualizedMuiTableRowReorderRuntimeContext | null =
        showRowDragCol && rowReorder
            ? {
                onReorder: rowReorder.onReorder,
                getDragHandleAriaLabel: rowReorder.getDragHandleAriaLabel,
                draggingRowIndex,
                setDraggingRowIndex,
                endRowDragSession,
            }
            : null;

    return (
        <VirtualizedMuiTableRowReorderContext.Provider value={rowReorderContextValue}>
            <TableVirtuoso
                className={className}
                style={{ height, width: '100%', minHeight: 0 }}
                data={data}
                components={components}
                fixedHeaderContent={fixedHeaderContent}
                defaultItemHeight={defaultItemHeight}
                increaseViewportBy={increaseViewportBy}
                computeItemKey={(index, row) => String(getRowKey(row as T, index))}
                itemContent={(index, row) => {
                    const r = row as T;
                    const rowKey = getRowKey(r, index);
                    const rowKeyStr = String(rowKey);
                    const canSelect =
                        showCheckbox &&
                        (!checkboxSelection?.isRowSelectable || checkboxSelection.isRowSelectable(r, index));
                    const rowChecked = selectedSet.has(rowKeyStr);
                    const reorderCtx = rowReorderContextValue;

                    const toggleRow = () => {
                        if (!checkboxSelection || !canSelect || loading) {
                            return;
                        }
                        const prev = checkboxSelection.selectedKeys as React.Key[];
                        if (rowChecked) {
                            checkboxSelection.onChange(prev.filter((k) => String(k) !== rowKeyStr));
                        } else {
                            checkboxSelection.onChange([...prev, rowKey]);
                        }
                    };

                    return (
                        <>
                            {showRowDragCol ? (
                                <TableCell
                                    key="__virtualized_row_drag__"
                                    padding="none"
                                    width={VIRTUALIZED_TABLE_ROW_DRAG_COL_WIDTH}
                                    sx={{
                                        borderBottom: '1px solid var(--stroke-primary-300)',
                                        width: VIRTUALIZED_TABLE_ROW_DRAG_COL_WIDTH,
                                        minWidth: VIRTUALIZED_TABLE_ROW_DRAG_COL_WIDTH,
                                        maxWidth: VIRTUALIZED_TABLE_ROW_DRAG_COL_WIDTH,
                                        verticalAlign: 'middle',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Box
                                        component="span"
                                        draggable
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData(VIRTUALIZED_TABLE_ROW_DND_MIME, String(index));
                                            e.dataTransfer.effectAllowed = 'move';
                                            try {
                                                e.dataTransfer.dropEffect = 'move';
                                            } catch {
                                                /* ignore */
                                            }
                                            const row = (e.currentTarget as HTMLElement).closest('tr');
                                            if (row instanceof HTMLTableRowElement) {
                                                setTableRowDragImage(e, row);
                                            }
                                            reorderCtx?.setDraggingRowIndex(index);
                                            setRowDndGlobalCursor(true);
                                        }}
                                        onDragEnd={() => reorderCtx?.endRowDragSession()}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'grab',
                                            touchAction: 'none',
                                            '&:active': {
                                                cursor: 'grabbing',
                                            },
                                            ...(reorderCtx?.draggingRowIndex === index
                                                ? { cursor: 'grabbing' }
                                                : {}),
                                            p: 1,
                                            color: 'var(--text-primary-300)',
                                            '& svg': { fontSize: 20 },
                                        }}
                                        aria-label={rowReorder?.getDragHandleAriaLabel?.() ?? 'Drag to reorder'}
                                    >
                                        <DragIndicatorIcon fontSize="small" />
                                    </Box>
                                </TableCell>
                            ) : null}
                            {showCheckbox ? (
                                <TableCell
                                    key="__virtualized_checkbox__"
                                    padding="none"
                                    width={checkboxWidth}
                                    sx={{
                                        borderBottom: '1px solid var(--stroke-primary-300)',
                                        width: checkboxWidth,
                                        minWidth: checkboxWidth,
                                        maxWidth: checkboxWidth,
                                        verticalAlign: 'middle',
                                        textAlign: 'center',
                                    }}
                                >
                                    {canSelect ? (
                                        <Checkbox
                                            size="small"
                                            color="primary"
                                            icon={<VirtualizedTableCheckboxBlankIcon />}
                                            checkedIcon={<VirtualizedTableCheckboxCheckedIcon />}
                                            checked={rowChecked}
                                            disabled={loading}
                                            onChange={toggleRow}
                                            onClick={(e) => e.stopPropagation()}
                                            sx={virtualizedTableCheckboxSx}
                                            inputProps={{
                                                'aria-label': `select row ${rowKeyStr}`,
                                            }}
                                        />
                                    ) : null}
                                </TableCell>
                            ) : null}
                            {columns.map((col) => (
                                <TableCell
                                    key={col.id}
                                    align={col.align ?? 'center'}
                                    width={col.width}
                                    sx={{
                                        borderBottom: '1px solid var(--text-dark)',
                                        textAlign: (col.align ?? 'center') as 'left' | 'center' | 'right',
                                    }}
                                >
                                    {col.renderCell(r, index)}
                                </TableCell>
                            ))}
                        </>
                    );
                }}
            />
        </VirtualizedMuiTableRowReorderContext.Provider>
    );
}
